import { usePrefs } from 'wsm-common-data';
import { useIsVertical } from './useIsVertical';
import { getImgSrc } from '../utils/images';

const useImage = ({ url, name, department, delayLoadImage, isWithinModal }) => {
	const {
		enableAutoFaceCrop,
		imageShape,
		squareImagePadding,
		imagePosition,
		defaultImageOverride
	} = usePrefs();
	const { isVertical } = useIsVertical();

	const isCircle = imageShape.toLowerCase() === 'circle';
	// This hardcoded value loads a 1px transparent gif, avoiding unnecessary network requests
	const placeholderImgSrc =
		'data:image/gif;base64,R0lGODlhAQABAIAAAAUEBAAAACwAAAAAAQABAAACAkQBADs=';
	const imgSrc = getImgSrc(
		enableAutoFaceCrop,
		url,
		isVertical,
		isWithinModal,
		defaultImageOverride
	);

	// Determine the type of image being displayed for accessibility
	const isUsingPlaceholder = !url;
	const isUsingCustomDefault = isUsingPlaceholder && defaultImageOverride;
	const isUsingStandardPlaceholder =
		isUsingPlaceholder && !defaultImageOverride;

	// Generate appropriate alt text based on image type
	let altTextPrefix = '';
	if (isUsingStandardPlaceholder) {
		altTextPrefix = 'Placeholder for ';
	} else if (isUsingCustomDefault) {
		altTextPrefix = 'Default image for ';
	}

	const imgProps = {
		// TODO: any a11y concerns/violations if two or more images have the same alt text?
		alt: `${altTextPrefix}Staff member ${name} from ${department}`,
		// Ensures React Boostrap <Image /> Component gets the correct prop based on imageShape pref
		[isCircle ? 'circle' : 'responsive']: true,
		// Using src and data-src in this way enables lazy-loading of images,
		// handled by cms-web here: https://ghe.coxautoinc.com/DDC-WebPlatform/cms-web/blob/master/htdocs/v9/media/js/ddc/v1/modules/lazy-image.js#L64
		src: delayLoadImage ? placeholderImgSrc : imgSrc,
		...(delayLoadImage && { 'data-src': imgSrc }),
		style: { objectPosition: imagePosition }
	};

	const getImgCls = () => {
		// TODO: this works for images in the dummy data currently in use.
		// In the future, we might need to expose a pref to customize this, which could be done by:
		// changing the object-position CSS property/class, or
		// potentially via Akamai IM params.
		const classes = [!isWithinModal ? 'h-100 w-100' : '', 'lazy-image'];

		// Sets padding for horizontal card photos regardless of `imageShape` or `deviceType`
		if (!isVertical && !isWithinModal) {
			classes.push('p-4');
		} else if (isWithinModal) {
			classes.push('p-5');
		} else {
			// Sets padding for vertical card photos on all devices ONLY when photos should NOT be full width.
			// The parentheses inside the if condition ensure the CORRECT operator precedence...
			classes.push(
				squareImagePadding === 'true' || isCircle ? 'p-5' : 'p-3'
			);
		}

		return classes.join(' ');
	};

	return { imgProps, imgCls: getImgCls() };
};

export default useImage;
